import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpResponse } from '@angular/common/http';
import { timeout } from 'rxjs/operators';

import { TextViewerComponent } from 'app/shared/text-viewer/text-viewer.component';
import { ObserverComponent } from 'app/logs/observer/observer.component';
import { DeploymentsService } from 'app/entities/FDEExtractorRunnerService/deployments/deployments.service';
import { RunningAccountService } from 'app/entities/FDERunningService/running-accounts/running-account.service';
import { IDeploymentRunner } from 'app/shared/model/FDEExtractorRunnerService/runner-command.model';
import { RunnerState, runnerState, runnerStateColor } from 'app/shared/util/runner-state';
import { Router } from '@angular/router';
import { IRunnerAlive } from 'app/shared/model/FDERunningAccountService/runner-alive.model';
import { IRunnerLogLine, IRunnerLogPage } from 'app/shared/model/FDERunnerLogsService/runner-log.model';
import { RunnerLogsService } from 'app/logs/logs.service';

const POLL_INTERVAL_MS = 2000;
const REQUEST_TIMEOUT_MS = 15000;
const RUNNERS_REFRESH_MS = 15000;
const MAX_BUFFER_LINES = 3000;
const ALL = '';
const DATED_LINE = /^\d{4}-\d{2}-\d{2} /;

@Component({
    selector: 'app-logs',
    standalone: true,
    imports: [CommonModule, FormsModule, TextViewerComponent, ObserverComponent],
    templateUrl: './logs.component.html',
    styleUrls: ['./logs.component.css']})
export class LogsComponent implements OnInit, OnDestroy {
    runners: IDeploymentRunner[] = [];
    running: IRunnerAlive[] = [];
    activeRunner = '';
    selectedAccount = ALL;
    selectedStream = ALL;

    text = '';
    error: string = null;
    follow = true;
    view = 'logs';
    droppedLines = 0;
    readonly maxBufferLines = MAX_BUFFER_LINES;

    private lines: IRunnerLogLine[] = [];
    private cursor: string = null;
    private generation = 0;
    private inFlight = false;
    private pollHandle;
    private runnersHandle;

    constructor(
        protected runnerLogsService: RunnerLogsService,
        protected deploymentsService: DeploymentsService,
        protected runningAccountService: RunningAccountService,
        protected router: Router
    ) {}

    ngOnInit() {
        this.loadRunners();
        this.pollHandle = setInterval(() => this.tick(), POLL_INTERVAL_MS);
        this.runnersHandle = setInterval(() => this.loadRunners(), RUNNERS_REFRESH_MS);
    }

    ngOnDestroy() {
        clearInterval(this.pollHandle);
        clearInterval(this.runnersHandle);
    }

    selectRunner(runner: IDeploymentRunner) {
        if (this.isInactive(runner) || this.activeRunner === runner.runnerCode) {
            return;
        }
        this.activeRunner = runner.runnerCode;
        this.selectedAccount = ALL;
        this.reset();
        this.loadRunning();
        this.poll();
    }

    onRunningAccountChange() {
        if (!this.selectedAccount) {
            this.reset();
            this.poll();
            return;
        }
        const alive = this.running.find(entry => entry.accountCode === this.selectedAccount);
        if (alive && alive.runnerCode && alive.runnerCode !== this.activeRunner) {
            this.activeRunner = alive.runnerCode;
        }
        this.reset();
        this.poll();
    }

    runnerOf(accountCode: string): string {
        const alive = this.running.find(entry => entry.accountCode === accountCode);
        return alive ? alive.runnerCode : null;
    }

    openAccount() {
        if (!this.selectedAccount) {
            return;
        }
        this.router.navigate(['/acct'], { queryParams: { accountCode: this.selectedAccount } });
    }

    onFilterChange() {
        this.reset();
        this.poll();
    }

    selectView(view: string) {
        this.view = view;
    }

    isObserver(): boolean {
        return this.view === 'observer';
    }

    hasRunners(): boolean {
        return this.runners.length > 0;
    }

    state(runner: IDeploymentRunner): RunnerState {
        return runnerState(runner);
    }

    stateColor(runner: IDeploymentRunner): string {
        return runnerStateColor(runnerState(runner));
    }

    isInactive(runner: IDeploymentRunner): boolean {
        return this.state(runner) !== 'RUNNING';
    }

    activeRunnerState(): string {
        const runner = this.runners.find(candidate => candidate.runnerCode === this.activeRunner);
        return runner && this.isInactive(runner) ? this.stateLabel(runner) : null;
    }

    stateLabel(runner: IDeploymentRunner): string {
        switch (this.state(runner)) {
            case 'RUNNING':
                return 'running';
            case 'STOPPED':
                return 'stopped';
            default:
                return 'unreachable';
        }
    }

    trackByRunnerCode(index: number, runner: IDeploymentRunner): string {
        return runner.runnerCode || String(index);
    }

    trackByAccountCode(index: number, entry: IRunnerAlive): string {
        return entry.accountCode || String(index);
    }

    private tick() {
        if (document.hidden || !this.activeRunner) {
            return;
        }
        this.loadRunning();
        if (this.isObserver()) {
            return;
        }
        this.poll();
    }

    private reset() {
        this.lines = [];
        this.cursor = null;
        this.text = '';
        this.error = null;
        this.droppedLines = 0;
        this.follow = true;
        this.generation = this.generation + 1;
        this.inFlight = false;
    }

    private loadRunners() {
        this.deploymentsService.listRunners().subscribe(
            (res: HttpResponse<IDeploymentRunner[]>) => {
                this.runners = (res.body || [])
                    .filter(runner => !!runner.runnerCode)
                    .sort((left, right) => left.runnerCode.localeCompare(right.runnerCode));
                if (!this.activeRunner) {
                    const active = this.runners.find(runner => !this.isInactive(runner));
                    if (active) {
                        this.selectRunner(active);
                    }
                }
            },
            () => (this.error = 'Unable to load the runner list')
        );
    }

    private sameRunning(incoming: IRunnerAlive[]): boolean {
        if (incoming.length !== this.running.length) {
            return false;
        }
        return incoming.every((entry, index) =>
            entry.accountCode === this.running[index].accountCode
            && entry.runnerCode === this.running[index].runnerCode);
    }

    private loadRunning() {
        this.runningAccountService.queryWatchdog().subscribe(
            (res: HttpResponse<IRunnerAlive[]>) => {
                const alive = (res.body || []).filter(entry => !!entry.accountCode);
                if (this.selectedAccount && !alive.some(entry => entry.accountCode === this.selectedAccount)) {
                    alive.push({ accountCode: this.selectedAccount, runnerCode: this.activeRunner });
                }
                alive.sort((left, right) => left.accountCode.localeCompare(right.accountCode));
                if (!this.sameRunning(alive)) {
                    this.running = alive;
                }
            },
            () => (this.running = this.selectedAccount
                ? [{ accountCode: this.selectedAccount, runnerCode: this.activeRunner }]
                : [])
        );
    }

    private poll() {
        if (this.inFlight || !this.activeRunner) {
            return;
        }
        this.inFlight = true;
        const generation = this.generation;
        this.runnerLogsService.query(this.activeRunner, this.selectedAccount, this.selectedStream, this.cursor)
            .pipe(timeout(REQUEST_TIMEOUT_MS))
            .subscribe(
                (res: HttpResponse<IRunnerLogPage>) => {
                    if (generation !== this.generation) {
                        return;
                    }
                    this.inFlight = false;
                    this.error = null;
                    this.absorb(res.body);
                },
                () => {
                    if (generation !== this.generation) {
                        return;
                    }
                    this.inFlight = false;
                    this.error = 'Log stream is unreachable, retrying';
                }
            );
    }

    private absorb(page: IRunnerLogPage) {
        if (!page) {
            return;
        }
        this.cursor = page.cursor || this.cursor;
        const incoming = page.lines || [];
        if (incoming.length === 0) {
            return;
        }
        this.lines = this.lines.concat(incoming);
        if (this.lines.length > MAX_BUFFER_LINES) {
            const overflow = this.lines.length - MAX_BUFFER_LINES;
            this.lines = this.lines.slice(overflow);
            this.droppedLines += overflow;
        }
        this.text = this.lines.map(line => this.render(line)).join('\n');
    }

    private render(line: IRunnerLogLine): string {
        const body = line.line || '';
        return DATED_LINE.test(body) ? body : this.formatTs(line.ts) + ' ' + body;
    }

    private formatTs(ts: number): string {
        if (!ts) {
            return '';
        }
        const date = new Date(ts);
        return (
            date.getFullYear() +
            '-' + this.pad(date.getMonth() + 1) +
            '-' + this.pad(date.getDate()) +
            ' ' + this.pad(date.getHours()) +
            ':' + this.pad(date.getMinutes()) +
            ':' + this.pad(date.getSeconds())
        );
    }

    private pad(value: number): string {
        return value < 10 ? '0' + value : String(value);
    }
}
